import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const MessageContext = createContext();

export const useMessage = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessage must be used within a MessageProvider');
  }
  return context;
};

export const MessageProvider = ({ children }) => {
  const [message, setMessage] = useState({ type: '', text: '', visible: false });

  const hideMessage = useCallback(() => {
    setMessage((prev) => ({ ...prev, visible: false }));
  }, []);

  const showMessage = useCallback((type, text, duration = 5000) => {
    let safeText = text;
    if (typeof text === 'object') {
      safeText = text?.message || JSON.stringify(text);
    }
    setMessage({ type, text: safeText, visible: true });

    if (duration > 0) {
      setTimeout(() => {
        hideMessage();
      }, duration);
    }
  }, [hideMessage]);

  const showSuccess = useCallback(
    (text, duration) => showMessage('success', text, duration),
    [showMessage]
  );
  const showError = useCallback(
    (text, duration) => showMessage('error', text, duration),
    [showMessage]
  );
  const showWarning = useCallback(
    (text, duration) => showMessage('warning', text, duration),
    [showMessage]
  );
  const showInfo = useCallback(
    (text, duration) => showMessage('info', text, duration),
    [showMessage]
  );

  const value = useMemo(
    () => ({
      message,
      showMessage,
      hideMessage,
      showSuccess,
      showError,
      showWarning,
      showInfo,
    }),
    [message, showMessage, hideMessage, showSuccess, showError, showWarning, showInfo]
  );

  return <MessageContext.Provider value={value}>{children}</MessageContext.Provider>;
};
